/* eslint-disable max-lines-per-function */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useGeolocation} from 'UI/hooks/useGeolocation';
import {useLocationSetting} from 'UI/hooks/useLocationSetting';

import type {FitPadding, FountainMarker, MapMarker} from './types';
import type * as Leaflet from 'leaflet';

/*
 * OpenFreeMap's "positron" vector style — genuinely free, no API key, no usage limits (unlike
 * CARTO/MapTiler/Stadia, whose free tiers require a key and/or are non-commercial-only). Routed
 * through our own `/api/map-vector-tiles` proxy (see that route) instead of fetched directly
 * from `tiles.openfreemap.org`, so every request (style, tiles, sprite, glyphs) goes through our
 * server — no third-party domain needs to be added to CSP, and the upstream is swappable in one
 * place. Unrelated to `/api/map-tiles`, which stays in place for the offline-download feature's
 * raster tile caching (see `src/sw.ts`).
 */
const VECTOR_STYLE_URL = '/api/map-vector-tiles/styles/positron';
/** Default fit-bounds padding (px) on each side, when a caller doesn't override it via `fitPadding`. */
const DEFAULT_FIT_PADDING: Required<FitPadding> = {
    bottom: 40,
    left: 40,
    right: 40,
    top: 40
};
/** Zoom level to center on when flying to the user's live location. */
const LOCATE_ZOOM = 16;
/** Minimum number of waypoints a walk needs before its route line is drawn. */
const MIN_ROUTE_POINTS = 2;
/** Caps the zoom level `fitBounds` will zoom in to, so a single marker (or tightly clustered markers) doesn't zoom in to the tile layer's technical maximum. Users can still zoom in further manually. */
const MAX_AUTO_FIT_ZOOM = 19;
/*
 * Hard zoom ceiling for the map itself. Must be set as a Leaflet `Map` option (not only on the
 * MapLibre GL layer): unlike `L.tileLayer`, the MapLibre GL Leaflet bridge layer doesn't extend
 * `L.GridLayer`, so it never runs Leaflet's `beforeAdd` -> `map._addZoomLimit()` registration —
 * `map.getMaxZoom()` would stay `Infinity`, which crashes `leaflet.markercluster` with "Map has
 * no maxZoom specified" as soon as a cluster is added.
 */
const MAP_MAX_ZOOM = 20;
/** Marker icon and route line color. */
const MARKER_COLOR = '#3D5A80';
/** Fountain marker color — distinct from `MARKER_COLOR` so drinking fountains read as a different kind of point. */
const FOUNTAIN_COLOR = '#1B98B0';
/*
 * MapLibre GL resolves its background worker script from its own `import.meta.url` at runtime —
 * inside a bundler that never correctly resolves to the package's real served location (tried:
 * `new URL('maplibre-gl/dist/maplibre-gl-worker.mjs', import.meta.url)`, which Next.js's webpack
 * rejects outright as "ESM packages need to be imported", even inside client-only code, because it
 * still compiles the referencing file for the server bundle). A plain string given directly to
 * `setWorkerUrl()` sidesteps bundler module resolution entirely — nothing here is ever parsed as an
 * import specifier. `maplibre-gl-worker.mjs` itself `import`s a sibling `./maplibre-gl-shared.mjs`,
 * so both files are copied as-is (unmodified, matching the installed `maplibre-gl` version) from
 * `node_modules/maplibre-gl/dist/` into `public/maplibre-gl/` and must be re-copied after upgrading
 * `maplibre-gl`.
 */
const ML_WORKER_URL = '/maplibre-gl/maplibre-gl-worker.mjs';

/**
 * Serializes marker coordinates into a stable string for shallow comparison.
 *
 * @param markers The markers to serialize.
 * @returns A string key representing the marker set.
 */
const getMarkersKey = (markers: MapMarker[]): string => markers.map(m => `${m.coordinates[0]},${m.coordinates[1]}`).join('|');

/**
 * Builds a marker pin icon, showing the waypoint's sequence number inside it when given.
 *
 * @param L      The dynamically imported Leaflet module.
 * @param number The marker's 1-based sequence number within its walk, if any.
 * @returns A Leaflet DivIcon for the marker.
 */
const createMarkerIcon = (L: typeof Leaflet, number?: number) => {
    const label = number === undefined
        ? ''
        : `<text x="14" y="15" text-anchor="middle" dominant-baseline="central" font-family="sans-serif" font-size="11" font-weight="700" fill="${MARKER_COLOR}">${number}</text>`;

    return L.divIcon({
        className: '',
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${MARKER_COLOR}" />
            <circle cx="14" cy="14" r="10" fill="#fff" />
            ${label}
        </svg>`,
        iconAnchor: [14, 40],
        iconSize: [28, 40],
        popupAnchor: [0, -40],
        tooltipAnchor: [0, -35]
    });
};

/**
 * Builds a round drinking-fountain badge icon (a water drop on a colored disc), distinct in
 * shape and color from the walk-waypoint pin so both read as different kinds of points at a
 * glance. Mirrors the `DrinkingFountainIcon` used on the toggle button.
 *
 * @param L The dynamically imported Leaflet module.
 * @returns A Leaflet DivIcon for the fountain marker.
 */
const createFountainIcon = (L: typeof Leaflet) => L.divIcon({
    className: '',
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="11" fill="${FOUNTAIN_COLOR}" stroke="#fff" stroke-width="2" />
        <path d="M12 4c-2.5 3-5 6.2-5 9.2a5 5 0 0 0 5 5.2 5 5 0 0 0 5-5.2c0-3-2.5-6.2-5-9.2Z" fill="#fff" />
    </svg>`,
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    tooltipAnchor: [0, -12]
});

/**
 * Custom hook for the `WalkMap` component.
 * Manages Leaflet map lifecycle, marker selection, live user location, and dynamic import of Leaflet.
 *
 * @param markers              Array of map markers to display.
 * @param selectedWalkIds      Optional walk IDs to filter markers by (`full` mode). Empty/omitted shows all markers.
 * @param onActiveMarkerChange Optional callback fired whenever the active marker changes.
 * @param fitPadding           Optional per-side auto-fit padding override, so callers with overlays (e.g. A filter bar, a walk slider) can reserve space around the markers.
 * @param fountains            Optional drinking-fountain points. Shown as their own toggleable layer (hidden by default, see `toggleFountains`), independent of `selectedWalkIds` and never tied to the info card.
 * @returns An object containing:
 * - `activeMarker`: The currently selected marker (shown in info card).
 * - `containerRef`: Ref for the map container div.
 * - `handleCloseCard`: Closes the info card.
 * - `handleFullscreenHintClick`: Enters fullscreen mode from the gesture hint overlay's button.
 * - `handleLocateMe`: Requests (or, once granted, re-centers on) the user's live location.
 * - `handleMarkerClick`: Selects a marker and opens the info card.
 * - `isFullscreen`: Whether the map is currently shown in fullscreen mode.
 * - `isReady`: Whether Leaflet has been loaded and the map is initialized.
 * - `mapRef`: Ref to the Leaflet map instance.
 * - `showFountains`: Whether the drinking-fountain layer is currently visible.
 * - `showGestureHint`: Whether the two-finger gesture hint overlay is currently shown (mobile, non-fullscreen).
 * - `toggleFountains`: Toggles the drinking-fountain layer.
 * - `toggleFullscreen`: Toggles fullscreen mode.
 *
 * @example
 * ```tsx
 * const {activeMarker, containerRef, isReady} = useWalkMap(markers);
 * ```
 */
export const useWalkMap = (
    markers: MapMarker[],
    selectedWalkIds?: string[] | null,
    onActiveMarkerChange?: (marker: MapMarker | null) => void,
    fitPadding?: FitPadding,
    fountains?: FountainMarker[]
) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<Leaflet.Map | null>(null);
    const markersLayerRef = useRef<Leaflet.MarkerClusterGroup | null>(null);
    const fountainsLayerRef = useRef<Leaflet.LayerGroup | null>(null);
    const routeLinesRef = useRef<Leaflet.Polyline[]>([]);
    const userMarkerRef = useRef<Leaflet.CircleMarker | null>(null);
    const accuracyCircleRef = useRef<Leaflet.Circle | null>(null);
    const initializingRef = useRef(false);
    const prevMarkersKeyRef = useRef<string>('');
    const pendingLocateRef = useRef(false);
    const isMobileRef = useRef(false);
    const [isReady, setIsReady] = useState(false);
    const [activeMarker, setActiveMarker] = useState<MapMarker | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFountains, setShowFountains] = useState(false);
    const [showGestureHint, setShowGestureHint] = useState(false);
    const {isEnabled: isLocationEnabled, setEnabled: setLocationEnabled} = useLocationSetting();
    const {position: userPosition} = useGeolocation(isLocationEnabled);
    const {t} = useTranslation();

    const mergedFitPadding = useMemo((): Required<FitPadding> => ({
        bottom: fitPadding?.bottom ?? DEFAULT_FIT_PADDING.bottom,
        left: fitPadding?.left ?? DEFAULT_FIT_PADDING.left,
        right: fitPadding?.right ?? DEFAULT_FIT_PADDING.right,
        top: fitPadding?.top ?? DEFAULT_FIT_PADDING.top
    }), [fitPadding?.bottom, fitPadding?.left, fitPadding?.right, fitPadding?.top]);

    const filteredMarkers = useMemo(
        () => {
            if (!selectedWalkIds || selectedWalkIds.length === 0) return markers;

            const selectedSet = new Set(selectedWalkIds);

            return markers.filter(m => m.walkId !== undefined && selectedSet.has(m.walkId));
        },
        [markers, selectedWalkIds]
    );

    /**
     * Handles clicking a marker — opens the info card for the given marker.
     *
     * @param marker The marker that was clicked.
     */
    const handleMarkerClick = useCallback((marker: MapMarker) => {
        setActiveMarker(marker);
        onActiveMarkerChange?.(marker);
    }, [onActiveMarkerChange]);

    /**
     * Closes the info card overlay.
     */
    const handleCloseCard = useCallback(() => {
        setActiveMarker(null);
        onActiveMarkerChange?.(null);
    }, [onActiveMarkerChange]);

    /**
     * Toggles fullscreen mode.
     */
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    /**
     * Toggles the drinking-fountain marker layer. Hidden by default — fountains are a
     * niche overlay most visitors won't need on every map view.
     */
    const toggleFountains = useCallback(() => {
        setShowFountains(prev => !prev);
    }, []);

    /**
     * Enters fullscreen mode from the gesture hint overlay's button, dismissing the hint itself.
     */
    const handleFullscreenHintClick = useCallback(() => {
        setShowGestureHint(false);
        setIsFullscreen(true);
    }, []);

    /**
     * Requests the user's live location if not yet enabled (prompting the browser's permission
     * dialog on first use), or re-centers the map on it if a position is already known. Never
     * happens on its own — only an explicit click here (or the effect below, once a fix arrives
     * for a click that had to wait) ever moves the map to the user's position.
     */
    const handleLocateMe = useCallback(() => {
        if (userPosition && mapRef.current) {
            mapRef.current.setView([userPosition.latitude, userPosition.longitude], LOCATE_ZOOM, {animate: true});

            return;
        }

        pendingLocateRef.current = true;
        if (!isLocationEnabled) setLocationEnabled(true);
    }, [isLocationEnabled, setLocationEnabled, userPosition]);

    /*
     * Flies to the user's position once a fix arrives, but only for a click that had to wait for
     * one (location just enabled, or already enabled but no fix yet) — never on mount just
     * because location happened to already be enabled from a previous visit.
     */
    useEffect(() => {
        if (!pendingLocateRef.current || !userPosition || !mapRef.current) return;

        pendingLocateRef.current = false;
        mapRef.current.setView([userPosition.latitude, userPosition.longitude], LOCATE_ZOOM, {animate: true});
    }, [userPosition]);

    /* Initialize Leaflet map */
    useEffect(() => {
        if (!containerRef.current || mapRef.current || initializingRef.current) return undefined;

        initializingRef.current = true;

        /** Augsburg Stadtzentrum. */
        const DEFAULT_CENTER: [number, number] = [48.3705, 10.8978];
        const DEFAULT_ZOOM = 13;

        /**
         * Async initialization of Leaflet to support SSR.
         */
        const init = async () => {
            // eslint-disable-next-line no-inline-comments
            const L = await import(/* webpackChunkName: "leaflet" */ 'leaflet');

            // @ts-expect-error CSS import handled by webpack at runtime
            // eslint-disable-next-line no-inline-comments
            await import(/* webpackChunkName: "leaflet-css" */ 'leaflet/dist/leaflet.css');

            if (!containerRef.current || mapRef.current) {
                initializingRef.current = false;

                return;
            }

            const map = L.map(containerRef.current, {
                center: DEFAULT_CENTER,
                dragging: !L.Browser.mobile,
                maxZoom: MAP_MAX_ZOOM,
                scrollWheelZoom: false,
                touchZoom: true,
                zoom: DEFAULT_ZOOM,
                zoomControl: true
            });

            /* Allow single-finger page scroll over the map on touch devices */
            map.getContainer().style.touchAction = 'pan-y';
            isMobileRef.current = L.Browser.mobile;

            // @ts-expect-error CSS import handled by webpack at runtime
            // eslint-disable-next-line no-inline-comments
            await import(/* webpackChunkName: "maplibre-gl-css" */ 'maplibre-gl/dist/maplibre-gl.css');
            // eslint-disable-next-line no-inline-comments
            const maplibregl = await import(/* webpackChunkName: "maplibre-gl" */ 'maplibre-gl');
            // eslint-disable-next-line no-inline-comments
            const mlLeaflet = await import(/* webpackChunkName: "ml-leaflet" */ '@maplibre/maplibre-gl-leaflet');
            const {maplibreGL} = mlLeaflet;

            maplibregl.setWorkerUrl(ML_WORKER_URL);

            /*
             * `attribution` is a standard Leaflet `Layer` option (merged at runtime by `L.Layer`'s
             * constructor and read by Leaflet's own attribution control) but isn't part of
             * `@maplibre/maplibre-gl-leaflet`'s narrower `LeafletMaplibreGLOptions` type — assigning
             * through a variable (rather than a fresh object literal at the call site) sidesteps
             * TypeScript's excess-property check without an unsafe cast. MapLibre's own built-in
             * attribution control is disabled so only Leaflet's shows, matching the previous raster setup.
             */
            const glLayerOptions = {
                attribution: '<a href="https://openfreemap.org">OpenFreeMap</a> '
                    + '&copy; <a href="https://www.openmaptiles.org/">OpenMapTiles</a> '
                    + 'Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                attributionControl: false as const,
                maxZoom: MAP_MAX_ZOOM,
                style: VECTOR_STYLE_URL
            };

            maplibreGL(glLayerOptions).addTo(map);

            // @ts-expect-error CSS import handled by webpack at runtime
            // eslint-disable-next-line no-inline-comments
            await import(/* webpackChunkName: "markercluster-css" */ 'leaflet.markercluster/dist/MarkerCluster.css');
            // @ts-expect-error CSS import handled by webpack at runtime
            // eslint-disable-next-line no-inline-comments
            await import(/* webpackChunkName: "mc-def-css" */ 'leaflet.markercluster/dist/MarkerCluster.Default.css');
            // eslint-disable-next-line no-inline-comments
            await import(/* webpackChunkName: "markercluster" */ 'leaflet.markercluster');

            // leaflet.markercluster attaches `markerClusterGroup` to the global `L` object at runtime
            // (see node_modules/leaflet/dist/leaflet-src.js: `window.L = exports`). Re-importing 'leaflet'
            // here returns a fixed-shape ES module namespace that never reflects that later mutation under
            // Turbopack, so read the real (mutable) global instead of the module namespace.
            const LC = (window as unknown as {L: typeof L}).L;

            markersLayerRef.current = LC.markerClusterGroup({
                disableClusteringAtZoom: 15,
                maxClusterRadius: 50,
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: true,
                /**
                 * Builds the badge icon for a marker cluster, showing its member count.
                 *
                 * @param cluster               The Leaflet marker cluster.
                 * @param cluster.getChildCount Returns the number of markers in the cluster.
                 * @returns A Leaflet DivIcon for the cluster badge.
                 */
                iconCreateFunction(cluster: {getChildCount(): number}) {
                    const count = cluster.getChildCount();

                    return L.divIcon({
                        className: '',
                        html: `<div style="
                            align-items:center;
                            background:#3D5A80;
                            border:3px solid #fff;
                            border-radius:50%;
                            box-shadow:0 2px 6px rgba(0,0,0,0.25);
                            color:#fff;
                            display:flex;
                            font-family:sans-serif;
                            font-size:16px;
                            font-weight:700;
                            height:36px;
                            justify-content:center;
                            width:36px;
                        ">${String(count)}</div>`,
                        iconAnchor: [18, 18],
                        iconSize: [36, 36]
                    });
                }
            }).addTo(map);
            fountainsLayerRef.current = L.layerGroup().addTo(map);
            mapRef.current = map;
            setIsReady(true);
        };

        void init();

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                markersLayerRef.current = null;
                fountainsLayerRef.current = null;
                routeLinesRef.current = [];
                userMarkerRef.current = null;
                accuracyCircleRef.current = null;
                initializingRef.current = false;
                setIsReady(false);
            }
        };
    }, []);

    /* Sync markers to map */
    useEffect(() => {
        if (!isReady || !mapRef.current || !markersLayerRef.current) return;

        const currentKey = getMarkersKey(filteredMarkers);

        if (currentKey === prevMarkersKeyRef.current) return;
        prevMarkersKeyRef.current = currentKey;

        /**
         * Async marker update — needs Leaflet module reference.
         */
        const syncMarkers = async () => {
            // eslint-disable-next-line no-inline-comments
            const L = await import(/* webpackChunkName: "leaflet" */ 'leaflet');

            if (!markersLayerRef.current || !mapRef.current) return;

            markersLayerRef.current.clearLayers();
            routeLinesRef.current.forEach(line => line.remove());
            routeLinesRef.current = [];

            const bounds = L.latLngBounds([]);
            const routeGroups = new Map<string, [number, number][]>();

            filteredMarkers.forEach(m => {
                const latLng: [number, number] = [m.coordinates[1], m.coordinates[0]];

                const marker = L.marker(latLng, {icon: createMarkerIcon(L, m.number)})
                    .bindTooltip(m.title, {
                        direction: 'top',
                        offset: [0, -4]
                    });

                marker.on('click', () => handleMarkerClick(m));

                markersLayerRef.current!.addLayer(marker);
                bounds.extend(latLng);

                if (m.walkId) {
                    const points = routeGroups.get(m.walkId) ?? [];

                    points.push(latLng);
                    routeGroups.set(m.walkId, points);
                }
            });

            routeGroups.forEach(points => {
                if (points.length < MIN_ROUTE_POINTS || !mapRef.current) return;

                routeLinesRef.current.push(
                    L.polyline(points, {
                        color: MARKER_COLOR,
                        dashArray: '6, 8',
                        opacity: 0.7,
                        weight: 3
                    }).addTo(mapRef.current)
                );
            });

            if (filteredMarkers.length > 0) {
                mapRef.current.fitBounds(bounds, {
                    maxZoom: MAX_AUTO_FIT_ZOOM,
                    paddingBottomRight: [mergedFitPadding.right, mergedFitPadding.bottom],
                    paddingTopLeft: [mergedFitPadding.left, mergedFitPadding.top]
                });
            }
        };

        void syncMarkers();
    }, [filteredMarkers, handleCloseCard, handleMarkerClick, isReady, mergedFitPadding]);

    /*
     * Sync drinking-fountain markers. Independent of `selectedWalkIds`/`filteredMarkers` — fountains
     * aren't tied to any single walk, so they stay visible no matter which walk is selected.
     */
    useEffect(() => {
        if (!isReady || !mapRef.current || !fountainsLayerRef.current) return;

        /**
         * Async fountain marker update — needs Leaflet module reference.
         */
        const syncFountains = async () => {
            // eslint-disable-next-line no-inline-comments
            const L = await import(/* webpackChunkName: "leaflet" */ 'leaflet');

            if (!fountainsLayerRef.current) return;

            fountainsLayerRef.current.clearLayers();

            if (!showFountains) return;

            (fountains ?? []).forEach(f => {
                const latLng: [number, number] = [f.coordinates[1], f.coordinates[0]];

                L.marker(latLng, {icon: createFountainIcon(L)})
                    .bindTooltip(t('walkMap.fountainLabel', {name: f.title}), {
                        direction: 'top',
                        offset: [0, -4]
                    })
                    .addTo(fountainsLayerRef.current!);
            });
        };

        void syncFountains();
    }, [fountains, isReady, showFountains, t]);

    /* Sync user location marker */
    useEffect(() => {
        if (!isReady || !mapRef.current) return;

        /**
         * Updates the user location dot and accuracy circle on the map.
         */
        const syncUserPosition = async () => {
            // eslint-disable-next-line no-inline-comments
            const L = await import(/* webpackChunkName: "leaflet" */ 'leaflet');

            if (!mapRef.current) return;

            if (!userPosition) {
                if (userMarkerRef.current) {
                    userMarkerRef.current.remove();
                    userMarkerRef.current = null;
                }

                if (accuracyCircleRef.current) {
                    accuracyCircleRef.current.remove();
                    accuracyCircleRef.current = null;
                }

                return;
            }

            const latLng: [number, number] = [userPosition.latitude, userPosition.longitude];

            if (userMarkerRef.current) {
                userMarkerRef.current.setLatLng(latLng);
            } else {
                userMarkerRef.current = L.circleMarker(latLng, {
                    color: '#FFFFFF',
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    radius: 7,
                    weight: 3
                }).addTo(mapRef.current);
            }

            if (accuracyCircleRef.current) {
                accuracyCircleRef.current.setLatLng(latLng);
                accuracyCircleRef.current.setRadius(userPosition.accuracy);
            } else {
                accuracyCircleRef.current = L.circle(latLng, {
                    color: '#4285F4',
                    fillColor: '#4285F4',
                    fillOpacity: 0.1,
                    radius: userPosition.accuracy,
                    weight: 1
                }).addTo(mapRef.current);
            }
        };

        void syncUserPosition();
    }, [isReady, userPosition]);

    /*
     * Mobile touch gesture handling. Embedded in the page, single-finger touches must scroll the
     * page rather than pan the map, so panning requires two fingers (with a hint overlay, offering
     * a button into fullscreen, shown on a single-finger touch). In fullscreen there's no
     * competing page scroll, so a single finger pans the map directly, like any native map app.
     */
    useEffect(() => {
        if (!isReady || !mapRef.current || !containerRef.current || !isMobileRef.current) return undefined;

        const map = mapRef.current;
        const container = containerRef.current;

        if (isFullscreen) {
            map.dragging.enable();
            container.style.touchAction = 'none';
            setShowGestureHint(false);

            return undefined;
        }

        map.dragging.disable();
        container.style.touchAction = 'pan-y';

        let hideTimeout: ReturnType<typeof setTimeout> | null = null;

        /**
         * Shows the two-finger hint overlay briefly after a single-finger touch.
         */
        const showHint = () => {
            setShowGestureHint(true);

            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = setTimeout(() => setShowGestureHint(false), 1500);
        };

        /**
         * Hides the two-finger hint overlay shortly after the touch ends.
         */
        const hideHintSoon = () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = setTimeout(() => setShowGestureHint(false), 800);
        };

        /**
         * Shows the hint on a single finger, enables map dragging on two fingers.
         *
         * @param e The touch event.
         */
        const handleTouchStart = (e: TouchEvent) => {
            const touchedMarker = (e.target as HTMLElement | null)?.closest('.leaflet-marker-icon');

            if (e.touches.length === 1) {
                if (!touchedMarker) showHint();
            } else {
                map.dragging.enable();
                container.style.touchAction = 'none';
                setShowGestureHint(false);
            }
        };

        /**
         * Resets dragging back to two-finger-only once the touch ends.
         */
        const handleTouchEnd = () => {
            map.dragging.disable();
            container.style.touchAction = 'pan-y';
            hideHintSoon();
        };

        container.addEventListener('touchstart', handleTouchStart, {passive: true});
        container.addEventListener('touchend', handleTouchEnd, {passive: true});

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchend', handleTouchEnd);
            if (hideTimeout) clearTimeout(hideTimeout);
        };
    }, [isFullscreen, isReady]);

    /* Exit fullscreen on Escape */
    useEffect(() => {
        if (!isFullscreen) return undefined;

        /**
         * Exits fullscreen mode when the user presses Escape.
         *
         * @param e The keyboard event.
         */
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsFullscreen(false);
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    /* Resize the Leaflet canvas after the fullscreen layout change settles */
    useEffect(() => {
        window.requestAnimationFrame(() => mapRef.current?.invalidateSize());
    }, [isFullscreen]);

    return {
        activeMarker,
        containerRef,
        filteredMarkers,
        handleCloseCard,
        handleFullscreenHintClick,
        handleLocateMe,
        handleMarkerClick,
        isFullscreen,
        isReady,
        mapRef,
        showFountains,
        showGestureHint,
        t,
        toggleFountains,
        toggleFullscreen
    };
};